import { html, nothing } from "lit";
import { t } from "../strings.js";

export type ShellEnvConfig = {
  enabled?: boolean;
  timeoutMs?: number;
};

export type EnvVarsProps = {
  vars: Record<string, string>;
  dirty: boolean;
  loading: boolean;
  saving: boolean;
  connected: boolean;
  onVarsChange: (vars: Record<string, string>) => void;
  onSave: () => void;
  onReload: () => void;
};

function renderVarsTable(
  vars: Record<string, string>,
  connected: boolean,
  onVarsChange: (vars: Record<string, string>) => void,
  onAdd: () => void,
) {
  const entries = Object.entries(vars);
  const handleKeyChange = (index: number, newKey: string) => {
    const keys = Object.keys(vars);
    const values = Object.values(vars);
    keys[index] = newKey;
    const next: Record<string, string> = {};
    keys.forEach((k, i) => {
      next[k] = values[i] ?? "";
    });
    onVarsChange(next);
  };
  const handleValueChange = (index: number, newValue: string) => {
    const keys = Object.keys(vars);
    const values = [...Object.values(vars)];
    values[index] = newValue;
    const next: Record<string, string> = {};
    keys.forEach((k, i) => {
      next[k] = values[i] ?? "";
    });
    onVarsChange(next);
  };
  const handleDelete = (index: number) => {
    const keys = Object.keys(vars).filter((_, i) => i !== index);
    const values = Object.values(vars).filter((_, i) => i !== index);
    const next: Record<string, string> = {};
    keys.forEach((k, i) => {
      next[k] = values[i] ?? "";
    });
    onVarsChange(next);
  };

  return html`
    ${entries.length === 0
      ? html`
          <p class="env-vars__empty">${t("envVarsEmpty")}</p>
          <button class="btn btn--bg-content" ?disabled=${!connected} @click=${onAdd}>
            ${t("envVarsAdd")}
          </button>
        `
      : html`
          <table class="env-vars__table">
            <thead>
              <tr>
                <th>${t("envVarsKey")}</th>
                <th>${t("envVarsValue")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${entries.map(
                ([key, value], index) => html`
                  <tr>
                    <td>
                      <span class="input"><input
                        class="env-vars__input"
                        type="text"
                        .value=${key}
                        placeholder=${t("envVarsKeyPlaceholder")}
                        ?disabled=${!connected}
                        @input=${(e: Event) => {
                          const target = e.target as HTMLInputElement;
                          handleKeyChange(index, target.value);
                        }}
                      /></span>
                    </td>
                    <td>
                      <span class="input"><input
                        class="env-vars__input"
                        type="text"
                        .value=${value}
                        placeholder=${t("envVarsValuePlaceholder")}
                        ?disabled=${!connected}
                        @input=${(e: Event) => {
                          const target = e.target as HTMLInputElement;
                          handleValueChange(index, target.value);
                        }}
                      /></span>
                    </td>
                    <td>
                      <button
                        class="btn small btn--ghost env-vars__delete"
                        ?disabled=${!connected}
                        @click=${() => handleDelete(index)}
                        title=${t("envVarsDelete")}
                      >
                        ${t("envVarsDelete")}
                      </button>
                    </td>
                  </tr>
                `,
              )}
            </tbody>
          </table>
          <button class="btn btn--bg-content env-vars__add" ?disabled=${!connected} @click=${onAdd}>
            ${t("envVarsAdd")}
          </button>
        `}
  `;
}

export function renderEnvVars(props: EnvVarsProps) {
  const { vars, dirty, loading, saving, connected, onVarsChange, onSave, onReload } = props;
  const canSave = connected && dirty && !saving && !loading;

  const handleVarsAdd = () => {
    onVarsChange({ ...vars, "": "" });
  };

  return html`
    <div class="env-vars">
      <div class="env-vars__toolbar">
        <button
          class="btn btn--bg-content"
          ?disabled=${loading || !connected}
          @click=${onReload}
          title=${t("overviewRefresh")}
        >
          ${loading ? "…" : t("overviewRefresh")}
        </button>
        <button
          class="btn btn--primary"
          ?disabled=${!canSave}
          @click=${onSave}
          title=${t("envVarsSave")}
        >
          ${saving ? "…" : t("envVarsSave")}
        </button>
      </div>
      ${dirty ? html`<p class="env-vars__dirty">${t("configUnsavedChanges")}</p>` : nothing}

      <div class="env-vars__sections">
        <section class="env-vars__section card" style="margin-bottom: 16px;">
          <h3 class="card-title" style="margin-bottom: 8px;">${t("envVarsSection")}</h3>
          <p class="muted" style="font-size: 12px; margin-bottom: 12px;">${t("configEnvVarsDesc")}</p>
          <div class="env-vars__list">
            ${renderVarsTable(vars, connected, onVarsChange, handleVarsAdd)}
          </div>
        </section>
      </div>
    </div>
  `;
}
